import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SLIDE_UP_EXIT_MS, useSlideUpPresence } from '../hooks/useSlideUpPresence';

export type SlideUpVariant = 'sheet' | 'fullscreen';

export type SlideUpOverlayProps = {
  open: boolean;
  onClose: () => void;
  variant?: SlideUpVariant;
  zIndex?: number;
  portalContainer?: HTMLElement | null;
  backdropClassName?: string;
  panelClassName?: string;
  exitMs?: number;
  role?: string;
  dir?: 'ltr' | 'rtl';
  'aria-label'?: string;
  /** Called once when overlay opens (after mount), not on every parent re-render. */
  onOpen?: () => void;
  children: React.ReactNode;
};

function joinClasses(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Shared slide-up overlay — bottom sheet (default) or fullscreen panel.
 * Animations: `src/styles/slide-up-motion.css`
 */
export function SlideUpOverlay({
  open,
  onClose,
  variant = 'sheet',
  zIndex,
  portalContainer,
  backdropClassName,
  panelClassName,
  exitMs = SLIDE_UP_EXIT_MS,
  role = 'dialog',
  dir,
  'aria-label': ariaLabel,
  onOpen,
  children,
}: SlideUpOverlayProps) {
  const { mounted, closing, requestClose } = useSlideUpPresence(open, exitMs);
  const openedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      openedRef.current = false;
      return;
    }
    if (mounted && !openedRef.current) {
      openedRef.current = true;
      onOpen?.();
    }
  }, [mounted, open, onOpen]);

  if (!mounted) return null;

  const isFullscreen = variant === 'fullscreen';

  const panel = (
    <div
      className={joinClasses(
        isFullscreen ? 'vo2-slide-up-fullscreen' : 'vo2-slide-up-panel',
        closing && (isFullscreen ? 'vo2-slide-up-fullscreen--closing' : 'vo2-slide-up-panel--closing'),
        panelClassName,
      )}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={event => { if (portalContainer && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); requestClose(onClose); } }}
      role={role}
      aria-modal={isFullscreen ? undefined : 'true'}
      aria-label={ariaLabel}
      dir={dir}
    >
      {children}
    </div>
  );

  if (isFullscreen) {
    return createPortal(
      <div style={zIndex != null ? { zIndex } : undefined}>{panel}</div>,
      portalContainer ?? document.body,
    );
  }

  return createPortal(
    <div
      className={joinClasses(
        'vo2-slide-up-backdrop',
        'mobile-sheet-backdrop',
        closing && 'vo2-slide-up-backdrop--closing',
        backdropClassName,
      )}
      style={zIndex != null ? { zIndex, '--vo2-slide-up-z': zIndex } as React.CSSProperties : undefined}
      onClick={() => requestClose(onClose)}
      role="presentation"
    >
      {panel}
    </div>,
    portalContainer ?? document.body,
  );
}
